import { useState, useEffect, useMemo, useCallback } from "react";
import {
  getTaskDefinitions,
  createTaskDefinition,
  updateTaskDefinition,
  deleteTaskDefinition,
  exportTaskDefinitionsCsv,
  downloadTaskDefinitionsCsv,
  extractErrorMessage,
  getTaskCategories,
  getTaskSubCategories,
  setTaskDefinitionEnabled,
} from "../lib/api";
import {
  PageHeader,
  DataTable,
  StatusBadge,
  LoadingSpinner,
  EmptyState,
  ErrorState,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
// EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
import { LINE_TAX_CLASSES, DEFAULT_LINE_TAX_CLASS } from "../lib/taxEnums";
// EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1
import { HelpPopover } from "../components/shared/HelpPopover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, ClipboardList, Pencil, Trash2, Download, ArrowUpDown, ArrowUp, ArrowDown, Search, X, EyeOff, Eye, AlertTriangle } from "lucide-react";
import { useTableDisplaySettings, getZoomClasses } from "../hooks/useZoom";
import { ZoomControl } from "../components/common/ZoomControl";
import PageZoomContainer from "../components/layout/PageZoomContainer";
// EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1
import { GuideButton } from "../components/common/GuideButton";

export default function TaskDefinitionsPage() {
  const { formatCurrency, currencySymbol } = useFormatters();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState("");
  // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1
  // 'enabled' (default), 'disabled', or 'all'. Stored in URL hash so admins
  // can deep-link the Disabled view from audit emails.
  const [enabledTab, setEnabledTab] = useState(() => {
    const h = (typeof window !== "undefined" ? window.location.hash : "") || "";
    if (h === "#disabled") return "disabled";
    if (h === "#all") return "all";
    return "enabled";
  });
  const [enableDialogTask, setEnableDialogTask] = useState(null);
  const [enableDialogReason, setEnableDialogReason] = useState("");
  const [enableDialogConfirmCritical, setEnableDialogConfirmCritical] = useState(false);
  const [enableDialogSubmitting, setEnableDialogSubmitting] = useState(false);
  
  // EF-EMAIL-SUBJECT-ROUTER-V1 + UI-ZOOM-EXTENSION: Zoom and wrap settings
  const { 
    currentZoom, scale, zoomIn, zoomOut, canZoomIn, canZoomOut,
    wrapText, toggleWrap 
  } = useTableDisplaySettings("task-definitions-page");
  const zoomClasses = getZoomClasses(scale);
  
  // EF-TASK-TYPE-REFORM-03: Task Categories state
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // EF-TASK-TYPE-REFORM-04: Form state - no legacy fields
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category_id: "",       // Required ID-based field
    subcategory_id: "",    // Required ID-based field
    default_charge_rate: "",
    default_charge_unit: "hourly",
    default_pay_rate: "",
    default_pay_unit: "hourly",
    is_active: true,
    // EF-TAX-MODEL: Tax/VAT fields
    tax_mode: "taxable",   // taxable, exempt, out_of_scope
    tax_rate: "0.20",      // VAT rate as decimal string
    // EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
    tax_class: DEFAULT_LINE_TAX_CLASS,
  });

  // EF-TASK-TYPE-REFORM-03: Load categories and subcategories
  const loadCategoriesAndSubCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const [catRes, subCatRes] = await Promise.all([
        getTaskCategories(),
        getTaskSubCategories(),
      ]);
      setCategories(catRes.data);
      setSubCategories(subCatRes.data);
    } catch (err) {
      console.error("Failed to load categories:", err);
      // Fallback - don't block the form if categories fail to load
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Filter subcategories when category changes
  useEffect(() => {
    if (formData.category_id) {
      const filtered = subCategories.filter(sc => sc.category_id === formData.category_id);
      setFilteredSubCategories(filtered);
      
      // If current subcategory doesn't belong to selected category, reset it
      if (formData.subcategory_id) {
        const stillValid = filtered.some(sc => sc.id === formData.subcategory_id);
        if (!stillValid) {
          setFormData(prev => ({ ...prev, subcategory_id: "" }));
        }
      }
    } else {
      setFilteredSubCategories([]);
    }
  }, [formData.category_id, formData.subcategory_id, subCategories]);

  useEffect(() => {
    loadTasks();
    loadCategoriesAndSubCategories();
  }, [loadCategoriesAndSubCategories]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(false);
      // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1
      // Admin page needs all task definitions (including disabled) so the
      // Enabled / Disabled / All tabs can be rendered without re-fetching.
      const response = await getTaskDefinitions({ limit: 10000, enabled_filter: "all" });
      setTasks(response.data);
    } catch (err) {
      console.error("Failed to load task definitions:", err);
      setError(true);
      toast.error("Failed to load task definitions");
    } finally {
      setLoading(false);
    }
  };

  // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1
  // Keep the URL hash in sync with the active tab so admins can share / bookmark.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const desired = enabledTab === "enabled" ? "" : `#${enabledTab}`;
    if (window.location.hash !== desired) {
      try {
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}${window.location.search}${desired}`,
        );
      } catch {
        /* no-op */
      }
    }
  }, [enabledTab]);

  // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1
  // A task is "rule-critical" if it is a default supplement or its
  // supplement_group is one the deterministic rule engine relies on.
  const isRuleCriticalTask = (task) => {
    if (!task) return false;
    if (task.is_default_supplement === true) return true;
    return ["meal", "crew_chief", "travel", "toll"].includes(
      (task.supplement_group || "").toLowerCase(),
    );
  };

  const openEnableDialog = (task) => {
    setEnableDialogTask(task);
    setEnableDialogReason("");
    setEnableDialogConfirmCritical(false);
  };

  const closeEnableDialog = () => {
    setEnableDialogTask(null);
    setEnableDialogReason("");
    setEnableDialogConfirmCritical(false);
    setEnableDialogSubmitting(false);
  };

  const handleConfirmEnableToggle = async () => {
    if (!enableDialogTask) return;
    const willEnable = enableDialogTask.is_enabled_for_new_shifts === false;
    const isCritical = isRuleCriticalTask(enableDialogTask);
    if (!willEnable && isCritical && !enableDialogConfirmCritical) {
      toast.error(
        "You must tick the deterministic-rule warning before disabling this task.",
      );
      return;
    }
    try {
      setEnableDialogSubmitting(true);
      await setTaskDefinitionEnabled(enableDialogTask.id, {
        enable: willEnable,
        hidden_reason: willEnable ? null : (enableDialogReason || "").trim() || null,
        confirm_disable_default_supplement: !willEnable && isCritical
          ? true
          : false,
      });
      toast.success(
        willEnable
          ? `Re-enabled "${enableDialogTask.name}" for new shift assignments.`
          : `Hidden "${enableDialogTask.name}" from new shift assignments.`,
      );
      closeEnableDialog();
      await loadTasks();
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setEnableDialogSubmitting(false);
    }
  };

  const handleOpenDialog = (task = null) => {
    if (task) {
      setSelectedTask(task);
      // EF-TAX-MODEL: Normalize tax_rate to match Select option values
      const taxRate = task.tax_rate ?? 0.20;
      let taxRateStr;
      if (taxRate === 0.2 || taxRate === 0.20) taxRateStr = "0.20";
      else if (taxRate === 0.1 || taxRate === 0.10) taxRateStr = "0.10";
      else if (taxRate === 0.055) taxRateStr = "0.055";
      else if (taxRate === 0.021) taxRateStr = "0.021";
      else if (taxRate === 0) taxRateStr = "0";
      else taxRateStr = taxRate.toFixed(2);
      
      setFormData({
        name: task.name,
        description: task.description || "",
        category_id: task.category_id || "",
        subcategory_id: task.subcategory_id || "",
        default_charge_rate: task.default_charge_rate.toString(),
        default_charge_unit: task.default_charge_unit,
        default_pay_rate: task.default_pay_rate.toString(),
        default_pay_unit: task.default_pay_unit,
        is_active: task.is_active,
        // EF-TAX-MODEL: Load tax fields
        tax_mode: task.tax_mode || "taxable",
        tax_rate: taxRateStr,
        // EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
        tax_class: task.tax_class || DEFAULT_LINE_TAX_CLASS,
      });
    } else {
      setSelectedTask(null);
      setFormData({
        name: "",
        description: "",
        category_id: "",
        subcategory_id: "",
        default_charge_rate: "",
        default_charge_unit: "hourly",
        default_pay_rate: "",
        default_pay_unit: "hourly",
        is_active: true,
        // EF-TAX-MODEL: Default tax fields
        tax_mode: "taxable",
        tax_rate: "0.20",
        // EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
        tax_class: DEFAULT_LINE_TAX_CLASS,
      });
    }
    setDialogOpen(true);
  };

  // EF-TASK-TYPE-REFORM-04: Submit only ID-based fields (no legacy)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields - only category is required
    if (!formData.category_id) {
      toast.error("Category is required");
      return;
    }
    
    try {
      const data = {
        name: formData.name,
        description: formData.description,
        category_id: formData.category_id,
        subcategory_id: formData.subcategory_id || null,  // Optional
        default_charge_rate: parseFloat(formData.default_charge_rate),
        default_charge_unit: formData.default_charge_unit,
        default_pay_rate: parseFloat(formData.default_pay_rate),
        default_pay_unit: formData.default_pay_unit,
        is_active: formData.is_active,
        // EF-TAX-MODEL: Tax/VAT fields
        tax_mode: formData.tax_mode,
        tax_rate: parseFloat(formData.tax_rate),
        // EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
        tax_class: formData.tax_class || null,
      };
      if (selectedTask) {
        await updateTaskDefinition(selectedTask.id, data);
        toast.success("Task definition updated successfully");
      } else {
        await createTaskDefinition(data);
        toast.success("Task definition created successfully");
      }
      setDialogOpen(false);
      loadTasks();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTaskDefinition(selectedTask.id);
      toast.success("Task definition deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedTask(null);
      loadTasks();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  // Sorting logic
  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Search and filter logic
  const filteredTasks = useMemo(() => {
    // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1
    // Apply enabled-tab filter first, then text search.
    const tabFiltered = tasks.filter((task) => {
      if (enabledTab === "all") return true;
      const enabled = task.is_enabled_for_new_shifts !== false; // default true
      return enabledTab === "enabled" ? enabled : !enabled;
    });

    if (!searchQuery.trim()) return tabFiltered;

    const query = searchQuery.toLowerCase().trim();
    return tabFiltered.filter(task => {
      // Get category and subcategory names for search
      const category = categories.find(c => c.id === task.category_id);
      const subcategory = task.subcategory_id 
        ? subCategories.find(s => s.id === task.subcategory_id)
        : null;
      
      return (
        task.name?.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query) ||
        category?.name?.toLowerCase().includes(query) ||
        subcategory?.name?.toLowerCase().includes(query) ||
        task.task_type?.toLowerCase().includes(query) ||
        task.supplement_group?.toLowerCase().includes(query)
      );
    });
  }, [tasks, searchQuery, categories, subCategories, enabledTab]);

  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks];
    sorted.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle special cases
      if (sortConfig.key === 'is_active') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredTasks, sortConfig]);

  // Sortable header component
  const SortableHeader = ({ columnKey, label }) => {
    const isActive = sortConfig.key === columnKey;
    return (
      <button
        onClick={() => handleSort(columnKey)}
        className="flex items-center gap-1 hover:text-primary transition-colors focus:outline-none"
        data-testid={`sort-${columnKey}`}
      >
        {label}
        {isActive ? (
          sortConfig.direction === 'asc' ? (
            <ArrowUp className="h-3 w-3 text-primary" />
          ) : (
            <ArrowDown className="h-3 w-3 text-primary" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    );
  };

  const columns = [
    { 
      key: "name", 
      label: <SortableHeader columnKey="name" label="Name" />,
      render: (value, row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDialog(row);
          }}
          className="text-left font-medium text-primary hover:underline focus:outline-none"
          data-testid={`task-name-link-${row.id}`}
        >
          {value}
        </button>
      ),
    },
    {
      key: "category_id",
      label: <SortableHeader columnKey="category_id" label="Type" />,
      render: (value, row) => {
        // Find category and subcategory names
        const category = categories.find(c => c.id === value);
        const subcategory = row.subcategory_id 
          ? subCategories.find(s => s.id === row.subcategory_id)
          : null;
        
        return (
          <div className="flex flex-col gap-0.5">
            <span className="capitalize font-mono text-xs">
              {category?.name || row.task_type || "—"}
            </span>
            {subcategory && (
              <span className="text-[10px] text-muted-foreground">
                {subcategory.name}
              </span>
            )}
            {!subcategory && row.supplement_group && (
              <span className="text-[10px] text-muted-foreground capitalize">
                {row.supplement_group.replace(/_/g, " ")}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "default_charge_rate",
      label: "Charge Rate",
      align: "right",
      render: (value, row) => (
        <span className="font-mono">
          {formatCurrency(value)}/{row.default_charge_unit === "hourly" ? "hr" : row.default_charge_unit}
        </span>
      ),
    },
    {
      key: "default_pay_rate",
      label: "Pay Rate",
      align: "right",
      render: (value, row) => (
        <span className="font-mono">
          {formatCurrency(value)}/{row.default_pay_unit === "hourly" ? "hr" : row.default_pay_unit}
        </span>
      ),
    },
    {
      key: "is_active",
      label: <SortableHeader columnKey="is_active" label="Status" />,
      render: (value, row) => {
        // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1
        // `is_active` = record exists. `is_enabled_for_new_shifts` controls
        // whether new shifts see it in selectors.
        const disabledForNewShifts = row.is_enabled_for_new_shifts === false;
        return (
          <div className="flex flex-col gap-1" data-testid={`task-status-cell-${row.id}`}>
            <StatusBadge status={value ? "active" : "inactive"} />
            {disabledForNewShifts && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-400"
                data-testid={`task-disabled-badge-${row.id}`}
                title={row.hidden_reason || "Hidden from new shift assignments"}
              >
                <EyeOff className="h-3 w-3" />
                Hidden from new shifts
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (_, row) => {
        const disabledForNewShifts = row.is_enabled_for_new_shifts === false;
        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                openEnableDialog(row);
              }}
              title={disabledForNewShifts ? "Re-enable for new shifts" : "Hide from new shifts"}
              data-testid={`toggle-enable-task-${row.id}`}
            >
              {disabledForNewShifts ? (
                <Eye className="h-4 w-4 text-emerald-400" />
              ) : (
                <EyeOff className="h-4 w-4 text-amber-400" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialog(row);
              }}
              data-testid={`edit-task-${row.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTask(row);
                setDeleteDialogOpen(true);
              }}
              data-testid={`delete-task-${row.id}`}
            >
              <Trash2 className="h-4 w-4 text-red-400" />
            </Button>
          </div>
        );
      },
    },
  ];

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load task definitions"
        message="We couldn't load the task definitions. This might be a temporary connection issue. Please try again."
        onRetry={loadTasks}
      />
    );
  }

  return (
    <div data-testid="task-definitions-page">
      {/* EF-GLOBAL-PAGE-ZOOM-FIX-V1: Wrap page content with zoom container */}
      <PageZoomContainer scale={scale}>
        <PageHeader
          title="Task Definitions"
          subtitle="Master task templates with default rates"
          actions={
            <div className="flex items-center gap-2">
              {/* EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1 */}
              <GuideButton testId="task-definitions-guide-btn" />
              <Button
                variant="outline"
                onClick={async () => {
                  // EVENTSFLO-TASK-DEFINITIONS-CSV-EXPORT-ALL-FIELDS-TAX-FIX-V1
                  // The endpoint requires admin auth, so we go through
                  // the axios client (Bearer token) and manually
                  // trigger the browser download. `window.open` did
                  // not attach the token and the response silently
                  // returned only the CSV header row.
                  try {
                    const res = await downloadTaskDefinitionsCsv();
                    const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    const today = new Date().toISOString().slice(0, 10);
                    a.download = `Task_Definitions_Export_${today}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    toast.error(extractErrorMessage(err) || "CSV export failed");
                  }
                }}
                className="rounded-sm font-medium tracking-wide uppercase text-xs px-4"
                data-testid="export-tasks-btn"
                disabled={tasks.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium tracking-wide uppercase text-xs px-6"
                data-testid="add-task-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            </div>
          }
        />

        {/* EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1 Tabs */}
        <div
          className="mb-3 flex items-center gap-1 border-b border-white/5 pb-2"
          role="tablist"
          data-testid="task-definitions-tabs"
        >
          {[
            { key: "enabled", label: "Enabled" },
            { key: "disabled", label: "Disabled / Hidden" },
            { key: "all", label: "All" },
          ].map((t) => {
            const count = tasks.filter((row) => {
              if (t.key === "all") return true;
              const enabled = row.is_enabled_for_new_shifts !== false;
              return t.key === "enabled" ? enabled : !enabled;
            }).length;
            const isActive = enabledTab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                type="button"
                aria-selected={isActive}
                onClick={() => setEnabledTab(t.key)}
                className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors focus:outline-none ${
                  isActive
                    ? "bg-primary/15 text-primary border border-primary/30"
                    : "text-muted-foreground hover:text-foreground border border-transparent"
                }`}
                data-testid={`task-definitions-tab-${t.key}`}
              >
                {t.label}
                <span className="ml-2 font-mono text-[10px] opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Search Bar */}
        <div className="mb-4 flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search task definitions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
              data-testid="search-tasks-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="clear-search-btn"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <span className="text-sm text-muted-foreground">
            {filteredTasks.length} of {tasks.length} tasks
          </span>
          
          {/* EF-EMAIL-SUBJECT-ROUTER-V1 + UI-ZOOM-EXTENSION: Zoom Controls */}
          <ZoomControl
            currentLabel={currentZoom.label}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            canZoomIn={canZoomIn}
            canZoomOut={canZoomOut}
            wrapText={wrapText}
            onToggleWrap={toggleWrap}
          />
        </div>

        <div className="bg-card border border-white/5 rounded-sm">
          {tasks.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="No task definitions yet"
              description="Define reusable task templates with charge and pay rates."
              action={
                <Button
                  onClick={() => handleOpenDialog()}
                  className="bg-primary text-primary-foreground"
                  data-testid="empty-add-task-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Task
                </Button>
              }
            />
          ) : filteredTasks.length === 0 ? (
            <EmptyState
              icon={Search}
              title="No matching tasks"
              description={`No task definitions match "${searchQuery}"`}
              action={
                <Button
                  variant="outline"
                  onClick={() => setSearchQuery("")}
                  data-testid="clear-search-empty-btn"
                >
                  Clear Search
                </Button>
              }
            />
          ) : (
            <div 
              className={`${!wrapText ? '[&_td]:whitespace-nowrap [&_td]:overflow-hidden [&_td]:text-ellipsis' : ''}`}
            >
              <DataTable columns={columns} data={sortedTasks} />
            </div>
          )}
        </div>
      </PageZoomContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-secondary text-xl uppercase tracking-wide">
              {selectedTask ? "Edit Task Definition" : "Add Task Definition"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Security Guard"
                required
                data-testid="task-name-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Task description"
                rows={2}
                data-testid="task-description-input"
              />
            </div>
            
            {/* EF-TASK-TYPE-REFORM-03: Category/SubCategory Cascading Dropdowns */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category_id">Task Category *</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    category_id: value,
                    subcategory_id: "", // Reset subcategory when category changes
                  })}
                  disabled={categoriesLoading}
                >
                  <SelectTrigger data-testid="task-category-select">
                    <SelectValue placeholder={categoriesLoading ? "Loading..." : "Select category..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="subcategory_id">Task SubCategory *</Label>
                <Select
                  value={formData.subcategory_id}
                  onValueChange={(value) => setFormData({ ...formData, subcategory_id: value })}
                  disabled={!formData.category_id || categoriesLoading}
                >
                  <SelectTrigger data-testid="task-subcategory-select">
                    <SelectValue placeholder={!formData.category_id ? "Select category first..." : "Select subcategory..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredSubCategories.map((subCat) => (
                      <SelectItem key={subCat.id} value={subCat.id}>
                        {subCat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {!formData.category_id && categories.length === 0 && !categoriesLoading && (
              <p className="text-xs text-amber-500">
                No categories found. Please configure task categories in admin settings.
              </p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="default_charge_rate">Charge Rate ({currencySymbol}) *</Label>
                <Input
                  id="default_charge_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.default_charge_rate}
                  onChange={(e) => setFormData({ ...formData, default_charge_rate: e.target.value })}
                  placeholder="25.00"
                  required
                  data-testid="task-charge-rate-input"
                />
              </div>
              <div>
                <Label htmlFor="default_charge_unit">Charge Unit</Label>
                <Select
                  value={formData.default_charge_unit}
                  onValueChange={(value) => setFormData({ ...formData, default_charge_unit: value })}
                >
                  <SelectTrigger data-testid="task-charge-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="per_item">Per Item</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="default_pay_rate">Pay Rate ({currencySymbol}) *</Label>
                <Input
                  id="default_pay_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.default_pay_rate}
                  onChange={(e) => setFormData({ ...formData, default_pay_rate: e.target.value })}
                  placeholder="15.00"
                  required
                  data-testid="task-pay-rate-input"
                />
              </div>
              <div>
                <Label htmlFor="default_pay_unit">Pay Unit</Label>
                <Select
                  value={formData.default_pay_unit}
                  onValueChange={(value) => setFormData({ ...formData, default_pay_unit: value })}
                >
                  <SelectTrigger data-testid="task-pay-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="per_item">Per Item</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* EF-TAX-MODEL: Tax/VAT Settings */}
            <div className="border-t border-white/10 pt-4 mt-2">
              <h4 className="text-sm font-medium text-muted-foreground mb-3">Tax / VAT Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tax_mode">Tax Mode</Label>
                  <Select
                    value={formData.tax_mode}
                    onValueChange={(value) => setFormData({ ...formData, tax_mode: value })}
                  >
                    <SelectTrigger data-testid="task-tax-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="taxable">Taxable (Subject to VAT)</SelectItem>
                      <SelectItem value="exempt">Exempt (VAT Exempt)</SelectItem>
                      <SelectItem value="out_of_scope">Out of Scope</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="tax_rate">VAT Rate</Label>
                  <Select
                    value={formData.tax_rate}
                    onValueChange={(value) => setFormData({ ...formData, tax_rate: value })}
                    disabled={formData.tax_mode !== "taxable"}
                  >
                    <SelectTrigger data-testid="task-tax-rate-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.20">20% (Standard)</SelectItem>
                      <SelectItem value="0.10">10% (Intermediate)</SelectItem>
                      <SelectItem value="0.055">5.5% (Reduced)</SelectItem>
                      <SelectItem value="0.021">2.1% (Super-reduced)</SelectItem>
                      <SelectItem value="0">0% (Zero-rated)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1 — Tax Class */}
              <div className="mt-4">
                <Label htmlFor="tax_class" className="flex items-center gap-2">
                  Tax Class
                  {/* EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1 */}
                  <HelpPopover testId="task-tax-class-help" title="What is Tax Class?">
                    <p>
                      Tax Class tells the <strong>Tax Rules Matrix</strong> how
                      to treat this task on quotes and invoices.
                    </p>
                    <p>
                      It does <strong>not</strong> change the task price or the
                      shift calculation — it only affects the VAT / TVA
                      decision on the resulting line.
                    </p>
                    <p>
                      Default tax classes may be <strong>auto-populated from
                      the task name</strong> by the admin classifier script.
                      Please review any unusual task manually — the automated
                      rules only cover clear, high-confidence patterns.
                    </p>
                    <p>
                      Unknown or unusual tasks should use
                      <strong> Other taxable item</strong> (safe default = 20%
                      TVA) or <strong> Manual review </strong>
                      (accountant checks before issuing).
                    </p>
                  </HelpPopover>
                </Label>
                <Select
                  value={formData.tax_class || DEFAULT_LINE_TAX_CLASS}
                  onValueChange={(value) => setFormData({ ...formData, tax_class: value })}
                >
                  <SelectTrigger data-testid="task-tax-class-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LINE_TAX_CLASSES.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Tax class is used with the client tax treatment to decide VAT/TVA treatment on quote and invoice lines.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="task-active-switch"
              />
              <Label htmlFor="is_active">Active</Label>
            </div>

            {/* EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1 */}
            {selectedTask && (
              <div
                className="border border-white/10 rounded-sm p-3 bg-black/30"
                data-testid="task-enable-panel"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {selectedTask.is_enabled_for_new_shifts === false ? (
                        <>
                          <EyeOff className="h-4 w-4 text-amber-400" />
                          Hidden from new shift assignments
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4 text-emerald-400" />
                          Available for new shift assignments
                        </>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Disabling does not delete history. Existing shifts,
                      quotes and invoices keep their saved snapshot.
                    </p>
                    {selectedTask.is_enabled_for_new_shifts === false &&
                      selectedTask.hidden_reason && (
                        <p
                          className="text-xs text-amber-300 mt-2"
                          data-testid="task-enable-panel-reason"
                        >
                          Reason: {selectedTask.hidden_reason}
                        </p>
                      )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      openEnableDialog(selectedTask);
                    }}
                    className="rounded-sm text-xs uppercase tracking-wide"
                    data-testid="task-enable-panel-toggle-btn"
                  >
                    {selectedTask.is_enabled_for_new_shifts === false
                      ? "Re-enable"
                      : "Disable for new shifts"}
                  </Button>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary text-primary-foreground" data-testid="task-submit-btn">
                {selectedTask ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task Definition</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTask?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-task-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1 Confirmation */}
      <AlertDialog
        open={enableDialogTask !== null}
        onOpenChange={(o) => {
          if (!o) closeEnableDialog();
        }}
      >
        <AlertDialogContent className="bg-card border-white/10 max-w-lg" data-testid="task-enable-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {enableDialogTask?.is_enabled_for_new_shifts === false ? (
                <>
                  <Eye className="h-5 w-5 text-emerald-400" />
                  Re-enable task definition
                </>
              ) : (
                <>
                  <EyeOff className="h-5 w-5 text-amber-400" />
                  Hide task definition from new shifts
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <span className="block">
                {enableDialogTask?.is_enabled_for_new_shifts === false ? (
                  <>
                    Re-enable <strong className="text-emerald-300">{enableDialogTask?.name}</strong>{" "}
                    so admins can pick it again when assigning new shifts. Pricing
                    snapshots on historical shifts remain unchanged.
                  </>
                ) : (
                  <>
                    Hide <strong className="text-amber-300">{enableDialogTask?.name}</strong>{" "}
                    from the assignment dropdowns for new shifts. Existing
                    historical shift line items, quotes and invoices that
                    reference this task keep their snapshot values — nothing
                    is deleted or recalculated.
                  </>
                )}
              </span>

              {enableDialogTask &&
                enableDialogTask.is_enabled_for_new_shifts !== false &&
                isRuleCriticalTask(enableDialogTask) && (
                  <span
                    className="block bg-red-950/60 border border-red-500/40 rounded-sm p-3 text-red-200"
                    data-testid="task-enable-confirm-critical-warning"
                  >
                    <span className="flex items-center gap-2 font-semibold mb-1">
                      <AlertTriangle className="h-4 w-4" />
                      Deterministic rule warning
                    </span>
                    <span className="block text-xs leading-relaxed">
                      This task is used by automatic deterministic rules
                      (group:&nbsp;
                      <code className="font-mono">
                        {enableDialogTask.supplement_group || "default supplement"}
                      </code>
                      ). Hiding it may stop automatic supplements from being
                      added to new shifts and may break Meal / Crew Chief /
                      Travel rule evaluation. Make sure another canonical
                      default supplement is configured first.
                    </span>
                    <label className="flex items-start gap-2 mt-2 text-xs">
                      <input
                        type="checkbox"
                        className="mt-1 accent-red-400"
                        checked={enableDialogConfirmCritical}
                        onChange={(e) =>
                          setEnableDialogConfirmCritical(e.target.checked)
                        }
                        data-testid="task-enable-confirm-critical-checkbox"
                      />
                      <span>
                        I understand this is a rule-critical task and want to
                        proceed.
                      </span>
                    </label>
                  </span>
                )}

              {enableDialogTask &&
                enableDialogTask.is_enabled_for_new_shifts !== false && (
                  <span className="block">
                    <Label
                      htmlFor="enable-dialog-reason"
                      className="text-xs uppercase tracking-wide text-muted-foreground"
                    >
                      Reason (optional, admin-only)
                    </Label>
                    <Textarea
                      id="enable-dialog-reason"
                      rows={2}
                      value={enableDialogReason}
                      onChange={(e) => setEnableDialogReason(e.target.value)}
                      placeholder="e.g. Replaced by Crew Chief Travel KM (new pricing 2026)"
                      data-testid="task-enable-confirm-reason"
                    />
                  </span>
                )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="task-enable-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmEnableToggle();
              }}
              disabled={
                enableDialogSubmitting ||
                // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1
                // When disabling a rule-critical task, lock the action until
                // the admin explicitly acknowledges the deterministic-rule
                // warning checkbox. Server still 409s as a backstop.
                (enableDialogTask &&
                  enableDialogTask.is_enabled_for_new_shifts !== false &&
                  isRuleCriticalTask(enableDialogTask) &&
                  !enableDialogConfirmCritical)
              }
              className={
                enableDialogTask?.is_enabled_for_new_shifts === false
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-amber-600 hover:bg-amber-700"
              }
              data-testid="task-enable-confirm-action"
            >
              {enableDialogSubmitting
                ? "Working..."
                : enableDialogTask?.is_enabled_for_new_shifts === false
                ? "Re-enable"
                : "Hide from new shifts"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
